import { useState } from "react";

import { HowItWorksAnimation } from "./HowItWorksAnimation";

/**
 * The landing tab: who built this, before what it is.
 *
 * Concept shift (user-directed, 2026-08-15): the page used to open with "FDS는 ~다" -
 * a lecture with the system as the subject - which read as educational material about
 * FDS rather than a portfolio about its author. The subject of the opening sentences is
 * now "저는 ~했다": an author hero (built/measured/deployed solo, everything public on
 * GitHub), then the list of things actually built with one design-intent line each,
 * then the three working beliefs with their receipts. The FDS explainer and the quiz
 * were NOT deleted - they are demoted into a collapsed "FDS가 처음이신가요?" accordion
 * (collapsed by default, content conditionally rendered so tests stay deterministic in
 * jsdom, where native <details> toggling is unreliable).
 *
 * Second pass (user-directed, 2026-08-15): the hero now opens with the author's name;
 * "혼자" emphasis was toned down to "직접" per feedback. The built-list leads with a
 * starred highlight - the in-process ML serving engine with its five safety features
 * (the role is AI/ML engineering, so the Triton-return-then-rebuild story goes first).
 * Every claim in that item is verified elsewhere: the five features map 1:1 to the act-5
 * interviewer notes in Overview.tsx and to MLPredictor's implementation.
 */

/**
 * The "good FDS" quiz (user-directed): show the obvious answers first, agree that they
 * are right, then raise the bar by one notch - the notch this whole project is about.
 * Every acknowledgement must genuinely concede the point before pivoting; a strawman
 * ("빠른 건 중요하지 않아요") would break the trust the pivot depends on.
 */
const QUIZ_ANSWERS: { id: string; icon: string; label: string; ack: string }[] = [
  {
    id: "accurate",
    icon: "🎯",
    label: "사기를 잘 맞히는 FDS!",
    ack: "맞아요, 잘 맞히는 건 기본이에요. 그런데 100번 중 99번 맞히는 AI도, 자기가 언제 틀리는지 모르면 남은 1번이 그대로 사고가 됩니다.",
  },
  {
    id: "fast",
    icon: "⚡",
    label: "빠른 FDS!",
    ack: "맞아요, 송금은 기다려주지 않으니까요. 이 시스템도 1건을 1,000분의 1초 수준으로 판정해요. 그런데 빠르게 틀리면, 느리게 틀리는 것보다 더 위험합니다.",
  },
  {
    id: "block-all",
    icon: "🧱",
    label: "사기를 100% 다 막는 FDS!",
    ack: "그럴 수 있다면 최고죠. 그런데 100% 막는 유일한 방법은 정상 송금까지 전부 막는 거예요. 그건 FDS가 아니라 은행 문을 닫는 겁니다.",
  },
];

const BELIEFS: { icon: string; claim: string; receipt: string; where: string }[] = [
  {
    icon: "🛑",
    claim: "\"AI가 얼마나 잘 맞히는가\"보다 \"AI를 언제 믿어도 되는가\"가 먼저다.",
    receipt:
      "그래서 이 시스템의 AI는 조건부 면허로 일합니다 - 안전 범위를 미리 측정해 두고, 운영 중 그 범위를 벗어나면 스스로 자동화를 멈추고 전부 사람에게 넘깁니다. 다시 켜는 것은 사람만 할 수 있어요.",
    where: "직접 해보기 탭의 🥊 MISSION 2에서 직접 공격해서 멈춰볼 수 있음",
  },
  {
    icon: "⚖️",
    claim: "기술은 이력서에 쓰려고 넣는 게 아니라, 직접 재보고 쓸지 말지 정하는 것이다.",
    receipt:
      "업계 표준 모델 서버인 NVIDIA Triton을 진짜로 설치해서 18,000건을 재봤습니다. 계산은 완벽히 일치했지만 이 작은 모델에는 약 190배 느려서 - 도입 대신 그 서버가 존재하는 이유였던 안전장치 5종을 벤치마킹해 직접 구현했고, 모델이 커지면 다시 도입할 조건까지 적어뒀어요.",
    where: "어떻게 만들었나 탭의 5막 + 매 커밋 자동 재검증(CI)",
  },
  {
    icon: "📄",
    claim: "실패는 지우는 게 아니라, 공개하는 것이다.",
    receipt:
      "학습 모델을 만들었지만 미리 봉투에 넣어둔 합격 기준에서 탈락했고, 그 불합격 성적표를 지우지 않고 그대로 공개했습니다. 시험이 끝난 뒤 채점 기준을 바꾸는 반칙은 이 프로젝트에 없습니다.",
    where: "직접 해보기 탭의 🤔 MISSION 4 (숫자로 보기)",
  },
];

const TECH: { icon: string; name: string; note: string }[] = [
  {
    icon: "⚡",
    name: "Python + FastAPI",
    note: "판정 두뇌. 송금 1건을 1,000분의 1초 수준으로 검사해요.",
  },
  {
    icon: "🗄️",
    name: "PostgreSQL",
    note: "판정의 블랙박스(비행기록장치). 같은 요청이 두 번 와도 판정은 딱 한 번만.",
  },
  {
    icon: "🧮",
    name: "순수 Python ML 서빙",
    note: "학습된 모델을 무거운 라이브러리 없이, 사람이 읽을 수 있는 JSON 계수로 서빙해요.",
  },
  {
    icon: "🖥️",
    name: "React + TypeScript",
    note: "지금 보고 계신 이 화면이에요.",
  },
  {
    icon: "📦",
    name: "Docker + Render",
    note: "어디서든 같은 모습으로 실행되게 포장해서 배포. 이 사이트가 그 결과물.",
  },
  {
    icon: "🚚",
    name: "ONNX + NVIDIA Triton",
    note: "직접 설치해 벤치마킹한 기술. 안 쓰기로 한 이유와 측정 기록을 전부 공개.",
  },
  {
    icon: "✅",
    name: "GitHub Actions CI",
    note: "코드를 올릴 때마다 전체 테스트와 수치 검증이 자동으로 다시 돌아요.",
  },
];

function GoodFdsQuiz() {
  const [picked, setPicked] = useState<string | null>(null);
  const pickedAnswer = QUIZ_ANSWERS.find((a) => a.id === picked);

  return (
    <section className="card">
      <h2>🤔 그럼, 좋은 FDS란 뭘까요?</h2>
      <p>하나 골라보세요. 어떤 답을 골라도 정답이에요. 진짜로요.</p>
      <div className="quiz-options" role="group" aria-label="좋은 FDS 고르기">
        {QUIZ_ANSWERS.map((a) => (
          <button
            key={a.id}
            className={`quiz-option${picked === a.id ? " quiz-option--picked" : ""}`}
            onClick={() => setPicked(a.id)}
          >
            {a.icon} {a.label}
          </button>
        ))}
      </div>

      {pickedAnswer && (
        <div className="quiz-reveal">
          <p>
            <strong>네, 맞아요!</strong> {pickedAnswer.ack}
          </p>
          <p>
            그래서 저는 저 답들에 <strong>한 줄을 더</strong> 요구합니다. 좋은 FDS는{" "}
            <strong>자기가 지금 믿을 만한 상태인지 스스로 아는 FDS</strong>예요. 잘
            맞히다가도 세상이 변해서 자신이 없어지면, 스스로 자동화를 멈추고 사람을
            부르는 것. 정확함이나 빠름과 달리, 이건 처음부터 그렇게 설계해야만 생기는
            성질이에요.
          </p>
          <p className="muted">
            이 사이트 전체가 그 한 줄의 증명입니다. 아래 &quot;제가 이 프로젝트로 말하고
            싶은 것&quot; 세 문장부터 보세요. 👇
          </p>
        </div>
      )}
    </section>
  );
}

interface Props {
  onTryDemo: () => void;
  /** Jumps straight to the playground's transfer mission - the 10-second verify. */
  onVerify: () => void;
}

export function Intro({ onTryDemo, onVerify }: Props) {
  const [basicsOpen, setBasicsOpen] = useState(false);

  return (
    <>
      <section className="card intro">
        <h2>👋 안녕하세요! 이동학입니다</h2>
        <p>
          실시간 이상거래탐지(FDS) 시스템 <strong>SafeFDS</strong>를{" "}
          <strong>직접 설계하고, 구현하고, 측정하고, 공개 배포했습니다.</strong> 지금 보고
          계신 이 사이트는 소개 페이지가 아니라, 그 시스템 위에서 실제로 돌아가는
          화면이에요.
        </p>
      </section>

      <button type="button" className="verify-banner" onClick={onVerify}>
        🔴 이 화면은 녹화된 데모가 아닙니다 — <strong>10초 만에 실제 연결 확인하기</strong> →
      </button>

      <section className="card">
        <details
          open={basicsOpen}
          onToggle={(e) => setBasicsOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary
            onClick={(e) => {
              e.preventDefault();
              setBasicsOpen((v) => !v);
            }}
          >
            🔰 <strong>FDS가 처음이신가요?</strong> — 3분 기초 설명과 미니 퀴즈{" "}
            {basicsOpen ? "접기 ▲" : "열기 ▼"}
          </summary>
          {basicsOpen && (
            <>
              <h3>이상거래탐지(FDS)가 뭔가요?</h3>
              <p>
                은행에는 매일 엄청난 수의 송금이 들어옵니다. 그중 대부분은 평범한 거래지만,
                아주 일부는
                <strong> 보이스피싱·명의도용 같은 사기 거래</strong>입니다. 이걸 한 건 한 건
                사람이 다 들여다보는 건 불가능합니다.
              </p>
              <p>
                <strong>FDS(Fraud Detection System, 이상거래탐지)</strong>는 공항 보안
                검색대와 비슷합니다. 사람들이 검색대를 지나가면 대부분은{" "}
                <strong>그냥 통과</strong>하고, 경보가 울린 몇 명만{" "}
                <strong>따로 불러 확인</strong>하죠. FDS도 송금이 들어오는 순간 위험도를
                즉시 판정해서, 안전한 거래는 바로 통과시키고{" "}
                <strong>의심스러운 거래만 사람에게</strong> 넘깁니다.
              </p>

              <HowItWorksAnimation />

              <p className="muted">
                이 방식의 핵심은 <strong>속도</strong>와 <strong>선택과 집중</strong>입니다.
                사람은 정말 판단이 필요한 소수의 거래에만 시간을 쓰고, 나머지는 시스템이
                실시간으로 처리합니다.
              </p>

              <GoodFdsQuiz />
            </>
          )}
        </details>
      </section>

      <section className="card">
        <h2>🧱 구현한 것들</h2>
        <p className="muted">
          모든 코드는 저장소 하나에 공개되어 있습니다. 각 항목의 &quot;왜 이렇게&quot;는
          &quot;어떻게 만들었나&quot; 탭에 이야기로 있습니다.
        </p>
        <ul className="plain built-list">
          <li className="built-highlight">
            <span className="built-badge">⭐ 가장 강조하고 싶은 것</span>
            <br />
            <strong>🧮 SF ML 엔진</strong> — 안전장치를 내장한 추론 계층입니다. 업계 표준
            모델 서버(NVIDIA
            Triton)를 직접 설치해 <strong>벤치마킹</strong>한 뒤, 모델 서버가 존재하는
            진짜 이유인 <strong>안전장치 5종</strong>(모델 지문 검증 · 골든 샘플 검산 ·
            스키마 검증 · 고장 시 사람에게 · 버전 감사 기록)을 이 모델 크기에 맞게 직접
            구현했습니다.
            <br />
            <span className="muted">
              설계 의도: 도구를 쓸 줄 아는 것을 넘어, 도구가 존재하는 이유를 이해하고 이
              시스템 크기로 다시 만들기 — 전 과정이 측정 기록과 함께 공개되어 있습니다.
            </span>
          </li>
          <li>
            <strong>⚖️ 실시간 판정 엔진</strong> — 송금 1건이 들어오면 단서 수집 → 위험
            점수 → 판정 → 저장까지 한 호흡에 처리합니다.
            <br />
            <span className="muted">
              설계 의도: 어떤 부품이 고장 나도 절대 &quot;그냥 통과&quot;로 넘어지지 않게 —
              모든 실패 경로가 사람에게로.
            </span>
          </li>
          <li>
            <strong>📱 3밴드 정책 + 자동 본인확인</strong> — 애매한 거래는 고객 폰으로
            자동으로 물어봅니다.
            <br />
            <span className="muted">
              설계 의도: 모델을 키우는 대신 새 관측을 만들자 — 사람 개입률 82.14% →
              55.54% (측정 기록 공개).
            </span>
          </li>
          <li>
            <strong>🪪 자동화 면허 발급기 + 런타임 거버너</strong> — 공격 시뮬레이션으로
            안전 범위를 재서 면허를 발급하고, 운영 중 범위를 벗어나면 스스로 정지합니다.
            <br />
            <span className="muted">
              설계 의도: &quot;얼마나 잘 맞히는가&quot;보다 &quot;언제 믿어도 되는가&quot;를
              먼저 증명하려고.
            </span>
          </li>
          <li>
            <strong>🧪 ML 파이프라인</strong> — 데이터 생성 → 학습 → 평가 → 적용 게이트.
            <br />
            <span className="muted">
              설계 의도: 시험 보기 전에 합격 기준을 고정 — 그래서 모델의 불합격 성적표가
              지금도 그대로 공개돼 있습니다.
            </span>
          </li>
          <li>
            <strong>✅ 테스트와 CI</strong> — 백엔드 180개·프론트 34개, 코드를 올릴 때마다
            전체 자동 재검증.
            <br />
            <span className="muted">
              설계 의도: 문서의 주장과 코드의 사실이 어긋나는 순간 커밋이 실패하게.
            </span>
          </li>
          <li>
            <strong>🖥️ 이 사이트 자체</strong> — React + TypeScript, 실서버 API에 직접
            연결.
            <br />
            <span className="muted">
              설계 의도: 스크린샷 모음이 아니라, 방문자의 클릭 하나하나가 진짜 판정이
              되게.
            </span>
          </li>
        </ul>
      </section>

      <section className="card">
        <h2>💬 SafeFDS 프로젝트에서 말하고 싶은 것</h2>
        <p className="muted">
          이 사이트는 기능 자랑이 아니라 <strong>일하는 방식의 증명</strong>이에요. 아래 세
          문장이 전부이고, 각 문장에는 이 사이트 안에서 직접 확인할 수 있는 영수증이 붙어
          있습니다.
        </p>
        <ol className="belief-list">
          {BELIEFS.map((b) => (
            <li key={b.claim} className="belief">
              <p className="belief-claim">
                <span className="belief-icon">{b.icon}</span> <strong>{b.claim}</strong>
              </p>
              <p>{b.receipt}</p>
              <p className="belief-where">🧾 영수증: {b.where}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="card">
        <h2>🧰 무엇으로 만들었나</h2>
        <div className="tech-grid">
          {TECH.map((t) => (
            <div key={t.name} className="tech-chip">
              <span className="tech-icon">{t.icon}</span>
              <div>
                <strong>{t.name}</strong>
                <p className="muted">{t.note}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="muted">
          왜 이 조합인지, 무엇을 일부러 안 썼는지(그게 더 어려운 결정이에요)는{" "}
          <strong>&quot;어떻게 만들었나&quot;</strong> 탭에 이야기로 적어뒀습니다.
        </p>
      </section>

      <section className="card">
        <h2>🗺️ 구조 한눈에</h2>
        <p className="intro-flow-line">
          💸 송금 도착 → 🔎 단서 모으기 → 🤖 위험 점수 → ⚖️ 판정 → ✅ 통과 · 📱 본인 확인 ·
          🧑‍💼 사람에게
        </p>
        <p>
          외우실 건 딱 하나예요: <strong>어느 부품이 고장 나도 화살표는 항상 &quot;사람에게&quot;
          쪽으로 넘어집니다.</strong> 절대 &quot;그냥 통과&quot; 쪽으로 넘어지지 않아요. 단계별
          상세와 고장 시 경로는 &quot;어떻게 만들었나&quot; 탭의 전체 구조도에 있습니다.
        </p>
      </section>

      <div className="cta-row">
        <button className="cta-button" onClick={onTryDemo}>
          🕹️ 직접 해보러 가기 — 송금하고, 공격하고, 부숴보세요 →
        </button>
        <p className="muted">가상의 시나리오지만, 클릭 하나하나가 진짜 서버의 진짜 판정입니다.</p>
      </div>
    </>
  );
}
